import { Typography, type TableColumnsType } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { type ReactNode } from 'react';
import utils from './utils';
import { type CustomerSetting } from '../entities';
import { type SiteMenuOrderView } from '../entities/site-menu-order';

const timeZone = process.env.REACT_APP_TIMEZONE;

/**
 * The earliest date, from now, a customer with "customerSetting" can place an order for: the day
 * after its deadline of "dueDaysToPlaceOrders" business days at "dueTimeToPlaceOrders"
 */
export const getEarliestOrderDate = (
  customerSetting:
    | Pick<CustomerSetting, 'dueDaysToPlaceOrders' | 'dueTimeToPlaceOrders'>
    | undefined
): Dayjs =>
  utils
    .addBusinessDays(
      dayjs().tz(timeZone),
      customerSetting?.dueDaysToPlaceOrders ?? 2,
      customerSetting?.dueTimeToPlaceOrders ?? '12:00'
    )
    .startOf('d')
    .add(1, 'd');
export interface RouteSubtotalRow {
  isRouteSubtotal: true;
  routeName?: string;
  totals: Record<string, number>;
  rowKey: string;
}

export const isRouteSubtotal = (record: unknown): boolean =>
  (record as RouteSubtotalRow)?.isRouteSubtotal;

export const ROUTE_SUBTOTAL_ROW_CLASS = 'route-subtotal-row';

export const routeSubtotalRowClassName = (record: unknown): string =>
  isRouteSubtotal(record) ? ROUTE_SUBTOTAL_ROW_CLASS : '';

const routeLabel = (routeName?: string): string =>
  routeName == null || routeName === '' ? 'No route total' : `${routeName} total`;

export const withRouteSubtotals = (
  rows: SiteMenuOrderView[],
  amounts: Record<string, (row: SiteMenuOrderView) => number>
): SiteMenuOrderView[] => {
  const amountKeys = Object.keys(amounts);
  // initialize the totals for a route subtotal row to 0 for each amount key
  const resetTotals = (): Record<string, number> =>
    amountKeys.reduce<Record<string, number>>((totals, key) => ({ ...totals, [key]: 0 }), {});

  const result: SiteMenuOrderView[] = [];
  let groupRoute: string | undefined;
  let groupTotals = resetTotals();
  let groupSize = 0;
  let groupIndex = 0;

  const closeGroup = (): void => {
    if (groupSize > 0) {
      result.push({
        isRouteSubtotal: true,
        routeName: groupRoute,
        totals: groupTotals,
        rowKey: `route-subtotal-${groupIndex}-${groupRoute ?? 'none'}`,
      } as unknown as SiteMenuOrderView);
      groupIndex += 1;
    }
    groupTotals = resetTotals();
    groupSize = 0;
  };

  rows.forEach((row, index) => {
    const route = row.route?.name;
    if (index > 0 && route !== groupRoute) {
      closeGroup();
    }
    groupRoute = route;
    amountKeys.forEach((key) => {
      groupTotals[key] += amounts[key](row) ?? 0;
    });
    groupSize += 1;
    result.push(row);
  });
  closeGroup();

  return result;
};

export const withRouteSubtotalCells = (
  columns: TableColumnsType<SiteMenuOrderView>,
  labelColumnIndex: number,
  routeSubTotalsColomnInedexes: Record<
    number,
    string | { amount: string; format: (value: number) => ReactNode }
  >
): TableColumnsType<SiteMenuOrderView> =>
  columns.map((column, columnIndex) => ({
    ...column,
    render: (value: any, record: any, index: number) => {
      if (isRouteSubtotal(record)) {
        if (columnIndex === labelColumnIndex) {
          return <Typography.Text strong>{routeLabel(record.routeName)}</Typography.Text>;
        }
        const columnKey = routeSubTotalsColomnInedexes[columnIndex];
        if (columnKey != null) {
          const amount = typeof columnKey === 'string' ? columnKey : columnKey.amount;
          const rendered =
            typeof columnKey === 'string'
              ? record.totals[amount]
              : columnKey.format(record.totals[amount] ?? 0);
          return <Typography.Text strong>{rendered}</Typography.Text>;
        }
        return null;
      }
      return column.render ? column.render(value, record, index) : value;
    },
  }));

export const totalMeals = (siteMenuOrders: ReadonlyArray<{ quantity: number }>): number =>
  siteMenuOrders.reduce((sum, siteMenuOrder) => sum + (siteMenuOrder.quantity ?? 0), 0);

interface PricedItem {
  quantity: number;
  unitPrice: number;
}

interface PricedSiteMenuOrder extends PricedItem {
  siteMenuBeverageOrders?: PricedItem[];
  siteMenuSupplyOrders?: PricedItem[];
}

const sumQuantities = (items: ReadonlyArray<PricedItem | undefined>): number =>
  items.reduce((sum, item) => sum + (item?.quantity ?? 0), 0);

const sumPrices = (items: ReadonlyArray<PricedItem | undefined>): number =>
  items.reduce((sum, item) => sum + (item?.unitPrice ?? 0) * (item?.quantity ?? 0), 0);

/** Total number of beverages, extra beverages included, ordered with "siteMenuOrders" */
export const totalBeverages = (siteMenuOrders: readonly PricedSiteMenuOrder[]): number =>
  sumQuantities(siteMenuOrders.flatMap((smo) => smo.siteMenuBeverageOrders ?? []));

/** Total number of supplies ordered with "siteMenuOrders" */
export const totalSupplies = (siteMenuOrders: readonly PricedSiteMenuOrder[]): number =>
  sumQuantities(siteMenuOrders.flatMap((smo) => smo.siteMenuSupplyOrders ?? []));

/** Price of "siteMenuOrders": the meals with the beverages and supplies ordered with them */
export const totalPrice = (siteMenuOrders: readonly PricedSiteMenuOrder[]): number =>
  sumPrices(siteMenuOrders) +
  sumPrices(siteMenuOrders.flatMap((smo) => smo.siteMenuBeverageOrders ?? [])) +
  sumPrices(siteMenuOrders.flatMap((smo) => smo.siteMenuSupplyOrders ?? []));
